import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import websiteSubscriptionService from '../../services/websiteSubscriptionService';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getPostalPlaceholder, isPostalRequired } from '../../utils/countryUtils';
import './WebsiteSubscription.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ onSuccess, onCancel, setParentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [billingDetails, setBillingDetails] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements) return;
    
    // Validate billing details
    if (!billingDetails.name.trim()) {
      setError('Cardholder name is required');
      setParentError('Cardholder name is required');
      return;
    }
    
    if (!billingDetails.address.trim() || !billingDetails.city.trim() ||
        !billingDetails.state.trim() || (isPostalRequired(billingDetails.country) && !billingDetails.zip.trim())) {
      setError('Complete billing address is required');
      setParentError('Complete billing address is required');
      return;
    }
    
    setLoading(true);
    setError('');
    setParentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error: paymentError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: billingDetails.name,
          address: {
            line1: billingDetails.address,
            city: billingDetails.city,
            state: billingDetails.state,
            postal_code: billingDetails.zip,
            country: billingDetails.country
          }
        }
      });

      if (paymentError) {
        setError(paymentError.message);
        setParentError(paymentError.message);
        setLoading(false);
        return;
      }

      const result = await websiteSubscriptionService.subscribe(paymentMethod.id);
      
      if (result.success) {
        onSuccess();
      } else {
        const errorMsg = result.error || 'Subscription failed';
        setError(errorMsg);
        setParentError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'An error occurred during subscription';
      setError(errorMsg);
      setParentError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="stripe-checkout-form">
      <div className="billing-info-section">
        <h4>Billing Information</h4>
        
        <div className="form-group">
          <label>Cardholder Name *</label>
          <input
            type="text"
            value={billingDetails.name}
            onChange={(e) => setBillingDetails({ ...billingDetails, name: e.target.value })}
            placeholder="John Doe"
            required
          />
        </div>

        <div className="form-group">
          <label>Billing Address *</label>
          <input
            type="text"
            value={billingDetails.address}
            onChange={(e) => setBillingDetails({ ...billingDetails, address: e.target.value })}
            placeholder="123 Main Street"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>City *</label>
            <input
              type="text"
              value={billingDetails.city}
              onChange={(e) => setBillingDetails({ ...billingDetails, city: e.target.value })}
              placeholder="New York"
              required
            />
          </div>

          <div className="form-group">
            <label>State/Province/Region *</label>
            <input
              type="text"
              value={billingDetails.state}
              onChange={(e) => setBillingDetails({ ...billingDetails, state: e.target.value })}
              placeholder={billingDetails.country === 'US' ? 'NY' : 'State/Province/Region'}
              maxLength={billingDetails.country === 'US' ? 2 : 50}
              required
            />
          </div>

          <div className="form-group">
            <label>ZIP/Postal Code {isPostalRequired(billingDetails.country) && '*'}</label>
            <input
              type="text"
              value={billingDetails.zip}
              onChange={(e) => setBillingDetails({ ...billingDetails, zip: e.target.value })}
              placeholder={getPostalPlaceholder(billingDetails.country)}
              required={isPostalRequired(billingDetails.country)}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Country *</label>
          <select
            value={billingDetails.country}
            onChange={(e) => setBillingDetails({ ...billingDetails, country: e.target.value })}
            required
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="MX">Mexico</option>
            <option value="CR">Costa Rica</option>
            <option value="NI">Nicaragua</option>
            <option value="SV">El Salvador</option>
            <option value="HN">Honduras</option>
            <option value="GT">Guatemala</option>
            <option value="BZ">Belize</option>
          </select>
        </div>
      </div>

      <div className="card-element-wrapper">
        <label>Card Details *</label>
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="checkout-error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      <div className="checkout-actions">
        <button 
          type="button" 
          onClick={onCancel}
          className="btn-cancel-checkout"
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          disabled={!stripe || loading}
          className="btn-submit-payment"
        >
          {loading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Processing...
            </>
          ) : (
            <>
              <i className="fas fa-lock"></i>
              Subscribe $4.99/month
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const WebsiteSubscription = ({ onSubscriptionSuccess, userRole }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  const handleSubscribe = () => {
    setShowCheckout(true);
    setError(null);
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setError(null);
  };

  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    onSubscriptionSuccess();
  };

  const features = [
    {
      icon: 'fas fa-globe',
      title: 'Professional Website',
      description: 'Get your own branded travel agent website with custom domain support'
    },
    {
      icon: 'fas fa-palette',
      title: 'Custom Themes',
      description: 'Choose from multiple professional themes and customize colors and branding'
    },
    {
      icon: 'fas fa-tags',
      title: 'Travel Categories',
      description: 'Showcase theme parks, cruises, activities, and custom travel packages'
    },
    {
      icon: 'fas fa-percent',
      title: 'Special Offers',
      description: 'Create and manage exclusive deals and promotions for your clients'
    },
    {
      icon: 'fas fa-blog',
      title: 'Travel Blog',
      description: 'Share travel tips, destination guides, and build your expertise'
    },
    {
      icon: 'fas fa-envelope',
      title: 'Lead Generation',
      description: 'Capture leads with quote request forms and contact integration'
    },
    {
      icon: 'fas fa-mobile-alt',
      title: 'Mobile Responsive',
      description: 'Your website looks perfect on all devices - desktop, tablet, and mobile'
    },
    {
      icon: 'fas fa-chart-line',
      title: 'Analytics & Insights',
      description: 'Track website performance and understand your client engagement'
    }
  ];

  return (
    <div className="website-subscription">
      <div className="subscription-container">
        {/* Header */}
        <div className="subscription-header">
          <div className="header-icon">
            <i className="fas fa-rocket"></i>
          </div>
          <h1>Boost Your Travel Business</h1>
          <p className="header-subtitle">
            Get your professional travel agent website and start attracting more clients today
          </p>
        </div>

        {/* Pricing Card */}
        <div className="pricing-section">
          <div className="pricing-card featured">
            <div className="pricing-header">
              <h3>Website Add-On</h3>
              <div className="price">
                <span className="currency">$</span>
                <span className="amount">4.99</span>
                <span className="period">/month</span>
              </div>
              <p className="price-description">Everything you need to grow your business online</p>
            </div>

            <div className="pricing-features">
              <div className="features-grid">
                {features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className="feature-icon">
                      <i className={feature.icon}></i>
                    </div>
                    <div className="feature-content">
                      <h4>{feature.title}</h4>
                      <p>{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pricing-footer">
              {!showCheckout ? (
                <button
                  className="btn-subscribe"
                  onClick={handleSubscribe}
                  disabled={loading}
                >
                  <i className="fas fa-credit-card"></i>
                  Start Your Website Today
                </button>
              ) : (
                <Elements stripe={stripePromise}>
                  <CheckoutForm 
                    onSuccess={handleCheckoutSuccess}
                    onCancel={handleCheckoutCancel}
                    setParentError={setError}
                  />
                </Elements>
              )}
              
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  {error}
                </div>
              )}

              <div className="subscription-notes">
                <p>
                  <i className="fas fa-shield-alt"></i>
                  Secure payment processing
                </p>
                <p>
                  <i className="fas fa-times-circle"></i>
                  Cancel anytime
                </p>
                <p>
                  <i className="fas fa-headset"></i>
                  24/7 support included
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Demo Section */}
        <div className="demo-section">
          <h2>See It In Action</h2>
          <p>Check out what your professional travel website could look like:</p>
          
          <div className="demo-links">
            <a 
              href="https://wharfsidetravel.tpcrm.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="demo-link"
            >
              <i className="fas fa-external-link-alt"></i>
              View Sample Website
            </a>
            <a 
              href="https://sarah-agent.tpcrm.io" 
              target="_blank" 
              rel="noopener noreferrer"
              className="demo-link"
            >
              <i className="fas fa-external-link-alt"></i>
              Another Example
            </a>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h4>How quickly can I get started?</h4>
              <p>Your website will be ready immediately after subscription. You can start customizing right away!</p>
            </div>
            
            <div className="faq-item">
              <h4>Can I use my own domain?</h4>
              <p>Yes! You can connect your custom domain or use our free subdomain (yourname.tpcrm.io).</p>
            </div>
            
            <div className="faq-item">
              <h4>Is technical knowledge required?</h4>
              <p>Not at all! Our easy-to-use interface lets you manage everything without any technical skills.</p>
            </div>
            
            <div className="faq-item">
              <h4>What about mobile visitors?</h4>
              <p>All websites are fully responsive and optimized for mobile, tablet, and desktop viewing.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteSubscription;
